// API types and interfaces for data services

export interface APIResponse<T> {
  data: T | null;
  error: string | null;
  source: 'live' | 'cached' | 'static';
  lastUpdated: Date | null;
  isStale: boolean;
}

export interface APIError {
  code: string;
  message: string;
  details?: string;
}

// World Bank API types
export interface WBIndicatorData {
  year: number;
  value: number | null;
  indicator: string;
  country: string;
}

export interface WBCountryData {
  id: string;
  iso2Code: string;
  name: string;
  region: { id: string; iso2code: string; value: string };
  incomeLevel: { id: string; iso2code: string; value: string };
  lendingType: { id: string; iso2code: string; value: string };
  capitalCity: string;
  longitude: string;
  latitude: string;
}

export interface WBAPIResponse<T> {
  page: number;
  pages: number;
  per_page: number;
  total: number;
  source: { id: string; value: string };
  data: T[];
}

// IMF API types
export interface IMFIndicatorValue {
  year: number;
  value: number;
}

export interface IMFCountryData {
  countryCode: string;
  indicatorCode: string;
  values: IMFIndicatorValue[];
}

export interface IMFAPIResponse {
  values: Record<string, Record<string, number>>;
  countries?: Record<string, string>;
  indicators?: Record<string, string>;
}

// FX API types
export interface FXRate {
  base: string;
  target: string;
  rate: number;
  change24h: number;
  changePercent24h: number;
  timestamp: Date;
}

export interface FXHistory {
  date: string;
  rate: number;
}

// Request/Response types
export interface FetchOptions {
  useCache?: boolean;
  cacheTTL?: number;
  retryCount?: number;
  timeout?: number;
}

export interface PaginatedRequest {
  page?: number;
  perPage?: number;
}

export interface TimeRangeRequest {
  startYear: number;
  endYear: number;
}

// World Bank indicator codes mapping
export const WB_INDICATOR_CODES: Record<string, string> = {
  // GDP & Growth
  gdp: 'NY.GDP.MKTP.CD',
  gdp_growth: 'NY.GDP.MKTP.KD.ZG',
  gdp_per_capita: 'NY.GDP.PCAP.CD',
  gdp_per_capita_ppp: 'NY.GDP.PCAP.PP.CD',
  gdp_constant: 'NY.GDP.MKTP.KD',
  gni: 'NY.GNP.MKTP.CD',
  gni_per_capita: 'NY.GNP.PCAP.CD',

  // FDI & Investment
  fdi_inflows: 'BX.KLT.DINV.CD.WD',
  fdi_outflows: 'BM.KLT.DINV.CD.WD',
  fdi_net: 'BN.KLT.DINV.CD',
  fdi_gdp: 'BX.KLT.DINV.WD.GD.ZS',
  gfcf_gdp: 'NE.GDI.FTOT.ZS',

  // Population & Demographics
  population: 'SP.POP.TOTL',
  population_growth: 'SP.POP.GROW',
  population_density: 'EN.POP.DNST',
  urban_population: 'SP.URB.TOTL.IN.ZS',
  life_expectancy: 'SP.DYN.LE00.IN',
  fertility_rate: 'SP.DYN.TFRT.IN',

  // Prices & Inflation
  inflation_cpi: 'FP.CPI.TOTL.ZG',
  inflation_gdp_deflator: 'NY.GDP.DEFL.KD.ZG',
  consumer_prices: 'FP.CPI.TOTL',

  // External Sector
  exports: 'NE.EXP.GNFS.CD',
  imports: 'NE.IMP.GNFS.CD',
  exports_gdp: 'NE.EXP.GNFS.ZS',
  imports_gdp: 'NE.IMP.GNFS.ZS',
  trade_gdp: 'NE.TRD.GNFS.ZS',
  current_account_gdp: 'BN.CAB.XOKA.GD.ZS',
  reserves: 'FI.RES.TOTL.CD',
  reserves_months_import: 'FI.RES.TOTL.MO',
  remittances: 'BX.TRF.PWKR.CD.DT',
  remittances_gdp: 'BX.TRF.PWKR.DT.GD.ZS',
  external_debt: 'DT.DOD.DECT.CD',
  external_debt_gdp: 'DT.DOD.DECT.GN.ZS',
  external_debt_gni: 'DT.DOD.DECT.GN.ZS',

  // Fiscal
  govt_debt_gdp: 'GC.DOD.TOTL.GD.ZS',
  govt_revenue_gdp: 'GC.REV.XGRT.GD.ZS',
  govt_expenditure_gdp: 'GC.XPN.TOTL.GD.ZS',
  tax_revenue_gdp: 'GC.TAX.TOTL.GD.ZS',

  // Labor Market
  unemployment: 'SL.UEM.TOTL.ZS',
  unemployment_youth: 'SL.UEM.1524.ZS',
  labor_force: 'SL.TLF.TOTL.IN',
  labor_participation: 'SL.TLF.CACT.ZS',
  employment_ratio: 'SL.EMP.TOTL.SP.ZS',

  // Business Environment
  ease_of_business: 'IC.BUS.EASE.XQ',
  time_start_business: 'IC.REG.DURS',
  cost_start_business: 'IC.REG.COST.PC.ZS',

  // Technology & Digital
  internet_users: 'IT.NET.USER.ZS',
  mobile_subscriptions: 'IT.CEL.SETS.P2',
  fixed_broadband: 'IT.NET.BBND.P2',

  // Education
  school_enrollment_primary: 'SE.PRM.ENRR',
  school_enrollment_secondary: 'SE.SEC.ENRR',
  school_enrollment_tertiary: 'SE.TER.ENRR',
  literacy_rate: 'SE.ADT.LITR.ZS',

  // Health
  health_expenditure_gdp: 'SH.XPD.CHEX.GD.ZS',
  hospital_beds: 'SH.MED.BEDS.ZS',
  physicians: 'SH.MED.PHYS.ZS',
};

// IMF indicator codes mapping
export const IMF_INDICATOR_CODES: Record<string, string> = {
  gdp_growth: 'NGDP_RPCH',
  gdp_nominal: 'NGDP',
  gdp_usd: 'NGDPD',
  gdp_per_capita_ppp: 'PPPPC',
  inflation: 'PCPIPCH',
  unemployment: 'LUR',
  current_account_gdp: 'BCA_NGDPD',
  govt_debt_gdp: 'GGXWDG_NGDP',
  fiscal_balance_gdp: 'GGXCNL_NGDP',
  govt_revenue_gdp: 'GGR_NGDP',
  govt_expenditure_gdp: 'GGX_NGDP',
  population: 'LP',
  exports_gdp: 'TXG_RPCH',
  imports_gdp: 'TMG_RPCH',
};

// Country code mappings - Central Asia & Silk Road Corridor
export const COUNTRY_CODES: Record<string, { wb: string; imf: string; name: string }> = {
  // Central Asia (Primary Focus)
  UZB: { wb: 'UZ', imf: 'UZB', name: 'Uzbekistan' },
  KAZ: { wb: 'KZ', imf: 'KAZ', name: 'Kazakhstan' },
  KGZ: { wb: 'KG', imf: 'KGZ', name: 'Kyrgyzstan' },
  TJK: { wb: 'TJ', imf: 'TJK', name: 'Tajikistan' },
  TKM: { wb: 'TM', imf: 'TKM', name: 'Turkmenistan' },
  // Caucasus (Silk Road Corridor)
  AZE: { wb: 'AZ', imf: 'AZE', name: 'Azerbaijan' },
  GEO: { wb: 'GE', imf: 'GEO', name: 'Georgia' },
  // Regional Peer (similar frontier profile)
  MNG: { wb: 'MN', imf: 'MNG', name: 'Mongolia' },
};

export const getWBCountryCode = (code: string): string => {
  return COUNTRY_CODES[code]?.wb || code;
};

export const getIMFCountryCode = (code: string): string => {
  return COUNTRY_CODES[code]?.imf || code;
};
